import {
  MESSAGE_LEVEL_MAP,
  TASK_STATUS_MAP,
} from "cortex/ui/src/constant/protobuf";
import {
  LEVEL_INFO,
  addErrorMessageAction,
  addMessageAction,
  addSuccessMessageAction,
} from "cortex/ui/src/redux-store/messages";
import { TaskStatus } from "cortex/ui/static/@cortex/messages/enums/task_status_pb";

import { payloadRouter } from "./protoUtils";

type TaskAction =
  | ReturnType<typeof addErrorMessageAction>
  | ReturnType<typeof addMessageAction>
  | ReturnType<typeof addSuccessMessageAction>;

type TaskEmitter = (action: TaskAction) => unknown;

interface TaskResultLike {
  errorsList?: string[] | null;
  source?: {
    id?: string | number;
    name?: string;
    type?: number;
  };
  status: number;
}

interface BulkDeviceMessageLike {
  level: number;
  message: string;
}

function errorsToHtmlStr(errorsList: string[] = []): string {
  return errorsList.reduce((acc, e) => {
    acc += `<li>${e}</li>`;

    return acc;
  }, "");
}

/*
 * You must bind a refreshMessage to consume this handler.
 * Do not use directly.
 */
function _genericTaskResult(
  refreshMessage: string | null,
  taskResult: TaskResultLike,
): TaskAction {
  const status = TASK_STATUS_MAP[taskResult.status];
  // Don't need get. `name` defaults to ''.
  const taskName = taskResult.source?.name || "Your";
  const errorsList = taskResult.errorsList || [];

  if (taskResult.status === TaskStatus.FAILURE) {
    const errors = errorsToHtmlStr(errorsList);
    const errorsHtml = errors ? ` <ul>${errors}</ul>` : "";

    return addErrorMessageAction(
      `${taskName} task has ${status}.${errorsHtml}`,
    );
  }

  if (taskResult.status === TaskStatus.SUCCESS) {
    let successMsg = `${taskName} task has ${status}.`;
    if (refreshMessage) {
      successMsg = `${successMsg} ${refreshMessage}`;
    }

    return addMessageAction(LEVEL_INFO, successMsg);
  }

  return addSuccessMessageAction(`${taskName} task has ${status}.`);
}

function bulkDeviceMessage(message: BulkDeviceMessageLike): TaskAction {
  const level = MESSAGE_LEVEL_MAP[message.level];

  return addMessageAction(level, message.message);
}

const bulkDeviceTaskResult = _genericTaskResult.bind(
  null,
  "Please refresh to see the updated devices.",
);

function bulkDeviceHandler(
  emitter: TaskEmitter,
  message: Uint8Array,
): unknown | undefined {
  const handlers = {
    message: bulkDeviceMessage,
    taskResult: bulkDeviceTaskResult,
  };

  const action = payloadRouter(message, handlers);

  if (action) {
    return emitter(action);
  }

  return undefined;
}

const genericTaskResult = _genericTaskResult.bind(null, null);

function genericTaskHandler(
  emitter: TaskEmitter,
  message: Uint8Array,
): unknown | undefined {
  const handlers = {
    taskResult: genericTaskResult,
  };

  const action = payloadRouter(message, handlers);

  if (action) {
    return emitter(action);
  }

  return undefined;
}

export {
  bulkDeviceHandler,
  bulkDeviceMessage,
  bulkDeviceTaskResult,
  genericTaskHandler,
  genericTaskResult,
};

export type { TaskAction };
